import { useCallback, useEffect, useState } from "react";

/**
 * Server-paginated state synced to URL query params. Used wherever the
 * upstream endpoint already accepts `?page=&perPage=` (Support tickets
 * / conversations / orgs, Server events, CRM tables, Sales leads).
 *
 * URL sync lets a refresh, a back-button, or a shared link round-trip
 * the page state — the prior ad-hoc `useState(1)` patterns lost that.
 *
 * Changing `perPage` snaps `page` back to 1 (consistent with
 * `usePagedList`).
 */
export interface UseServerPaginationOptions {
  /** Initial / fallback rows-per-page. Default `25`. */
  defaultPerPage?: number;
  /** Query string key for the current page. Default `"page"`. */
  pageParam?: string;
  /** Query string key for the per-page setting. Default `"perPage"`. */
  perPageParam?: string;
}

export interface UseServerPaginationResult {
  page: number;
  perPage: number;
  setPage: (p: number) => void;
  setPerPage: (n: number) => void;
  /** Restore page 1 and the default per-page. */
  reset: () => void;
}

function readSearch(): URLSearchParams {
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

function writeSearch(
  pageParam: string,
  perPageParam: string,
  page: number,
  perPage: number,
  defaultPerPage: number,
): void {
  if (typeof window === "undefined") return;
  const search = new URLSearchParams(window.location.search);
  if (page === 1) search.delete(pageParam);
  else search.set(pageParam, String(page));
  if (perPage === defaultPerPage) search.delete(perPageParam);
  else search.set(perPageParam, String(perPage));
  const qs = search.toString();
  const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", url);
}

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw == null) return fallback;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return n;
}

export function useServerPagination(
  options: UseServerPaginationOptions = {},
): UseServerPaginationResult {
  const {
    defaultPerPage = 25,
    pageParam = "page",
    perPageParam = "perPage",
  } = options;

  const [page, setPageState] = useState<number>(() =>
    parsePositiveInt(readSearch().get(pageParam), 1),
  );
  const [perPage, setPerPageState] = useState<number>(() =>
    parsePositiveInt(readSearch().get(perPageParam), defaultPerPage),
  );

  useEffect(() => {
    writeSearch(pageParam, perPageParam, page, perPage, defaultPerPage);
  }, [page, perPage, pageParam, perPageParam, defaultPerPage]);

  const setPage = useCallback((p: number): void => {
    setPageState(Math.max(1, Math.floor(p)));
  }, []);

  const setPerPage = useCallback((n: number): void => {
    setPerPageState(Math.max(1, Math.floor(n)));
    setPageState(1);
  }, []);

  const reset = useCallback((): void => {
    setPageState(1);
    setPerPageState(defaultPerPage);
  }, [defaultPerPage]);

  return { page, perPage, setPage, setPerPage, reset };
}
